<!-- eslint-disable -->
<script>
/* eslint-disable no-underscore-dangle, no-unused-expressions */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import voiceSIPClient from 'dashboard/helper/VoiceSIPClient';
import voiceSipSessionAPI from 'dashboard/api/voiceSipSession';
import { useStore } from 'dashboard/composables/store';
import Auth from 'dashboard/api/auth';

const DEFAULT_WS_SERVER = 'wss://demo.1980tic.cloud:443/ws';
const DEFAULT_DOMAIN = 'demo.1980tic.cloud';
const sipCredentialKey = (accountId, userId) => `vc_sip_${accountId}_${userId}`;

export default {
  name: 'VoiceCallModals',
  setup() {
    const store = useStore();

    // --- State ---
    const showIncomingCall = ref(false);
    const showActiveCall = ref(false);
    const incomingCallFrom = ref('');
    const incomingSession = ref(null);
    const activeCallNumber = ref('');
    const activeSession = ref(null);
    const callStatus = ref('');
    const callDuration = ref(0);
    const callTimer = ref(null);
    const activeCallMeta = ref(null);
    const isHangingUp = ref(false);
    const isMuted = ref(false);
    const isOnHold = ref(false);
    const remoteAudio = ref(null);

    // Dialer
    const showDialer = ref(false);
    const dialerNumber = ref('');
    const dialerError = ref('');
    const dialPadKeys = [
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '*',
      '0',
      '#',
    ];

    // Transfer
    const showTransferPanel = ref(false);
    const transferTarget = ref('');

    // Presencia SIP — agentes registrados (recibidos por BroadcastChannel)
    const sipOnlineAgents = ref({}); // { userId: { name, extension, lastSeen } }
    const AGENT_STALE_MS = 90000; // 90s sin heartbeat → considerado offline

    const connectedAgents = computed(() => {
      const now = Date.now();
      return Object.values(sipOnlineAgents.value)
        .filter(a => now - a.lastSeen < AGENT_STALE_MS)
        .sort((a, b) => a.name.localeCompare(b.name));
    });

    const updatePresence = (userId, name, extension, timestamp) => {
      sipOnlineAgents.value = {
        ...sipOnlineAgents.value,
        [userId]: { name, extension, lastSeen: timestamp || Date.now() },
      };
    };
    const removePresence = userId => {
      const copy = { ...sipOnlineAgents.value };
      delete copy[userId];
      sipOnlineAgents.value = copy;
    };

    // Estado de registro SIP del usuario actual
    const sipConfigured = ref(false); // true = tiene credenciales guardadas
    const sipRegistered = ref(false); // true = JsSIP registrado exitosamente
    const sipCurrentExt = ref('');
    const showSipActiveElsewhere = ref(false);
    const sipActiveElsewhere = ref(null);
    const isTakingOverSip = ref(false);

    // Modal de configuración SIP por usuario
    const showSipConfig = ref(false);
    const showSipAdvanced = ref(false);
    const sipRegError = ref('');
    const sipConfigForm = ref({
      websocket_server: DEFAULT_WS_SERVER,
      domain: DEFAULT_DOMAIN,
      sipUser: '',
      password: '',
    });

    const readLocalSipConfig = (accountId, userId) => {
      try {
        return JSON.parse(
          localStorage.getItem(sipCredentialKey(accountId, userId)) || 'null'
        );
      } catch {
        return null;
      }
    };

    const cacheSipConfigLocally = (accountId, userId, config) => {
      if (!config?.extension || !config?.password) return;
      try {
        localStorage.setItem(
          sipCredentialKey(accountId, userId),
          JSON.stringify(config)
        );
      } catch {
        /* local storage unavailable */
      }
    };

    const applySipConfigToForm = config => {
      sipConfigForm.value.websocket_server =
        config?.websocket_server || DEFAULT_WS_SERVER;
      sipConfigForm.value.domain = config?.domain || DEFAULT_DOMAIN;
      sipConfigForm.value.sipUser = config?.extension || '';
      sipConfigForm.value.password = config?.password || '';
    };

    const buildSipConfigPayload = currentUser => ({
      websocket_server:
        sipConfigForm.value.websocket_server || DEFAULT_WS_SERVER,
      domain: sipConfigForm.value.domain || DEFAULT_DOMAIN,
      extension: sipConfigForm.value.sipUser,
      password: sipConfigForm.value.password,
      display_name: currentUser.name,
    });

    const loadPersistedSipConfig = async () => {
      const cu = store.getters['getCurrentUser'];
      const aid = store.getters['getCurrentAccountId'];
      if (!cu?.id || !aid) return null;

      try {
        const { data } = await voiceSipSessionAPI.getConfig();
        if (data?.configured && data.sip_config?.extension) {
          cacheSipConfigLocally(aid, cu.id, data.sip_config);
          return data.sip_config;
        }
      } catch {
        /* fallback to local cache */
      }

      return readLocalSipConfig(aid, cu.id);
    };

    const refreshSipConfigState = async () => {
      const config = await loadPersistedSipConfig();
      if (config?.extension && config?.password) {
        sipConfigured.value = true;
        sipCurrentExt.value = config.extension;
      }
    };

    // Pre-rellena el formulario con credenciales existentes al abrir el modal
    const openSipConfig = async () => {
      sipRegError.value = '';
      const stored = await loadPersistedSipConfig();
      applySipConfigToForm(stored);
      showSipConfig.value = true;
    };

    const saveSipConfig = async () => {
      const currentUser = store.getters['getCurrentUser'];
      const accountId = store.getters['getCurrentAccountId'];
      if (!currentUser?.id || !accountId) {
        sipRegError.value = 'Error: sesión no disponible, recarga la página';
        return;
      }
      if (!sipConfigForm.value.sipUser || !sipConfigForm.value.password) return;
      sipRegError.value = '';
      const sipConfig = buildSipConfigPayload(currentUser);

      try {
        await voiceSipSessionAPI.saveConfig({ sip_config: sipConfig });
        cacheSipConfigLocally(accountId, currentUser.id, sipConfig);
      } catch (_e) {
        sipRegError.value = 'No se pudo guardar la configuracion SIP';
        return;
      }

      // Actualizar phone_number en el perfil del agente para que aparezca en la lista de agentes
      try {
        await Auth.profileUpdate({ phone_number: sipConfigForm.value.sipUser });
        await store.dispatch('agents/get');
      } catch (e) {
        // handled silently
      }

      showSipConfig.value = false;
      sipRegistered.value = false;
      sipConfigured.value = true;
      sipCurrentExt.value = sipConfig.extension;
      // Re-inicializar JsSIP con las nuevas credenciales
      if (window.voiceSIPReinit) window.voiceSIPReinit();
    };

    // Device
    const showDevicePanel = ref(false);
    const audioInputDevices = ref([]);
    const audioOutputDevices = ref([]);
    const selectedInputId = ref('');
    const selectedOutputId = ref('');

    const panelPositions = ref({});
    const dragState = ref(null);

    const clampPanelPosition = (x, y, width, height) => {
      const margin = 12;
      return {
        x: Math.min(Math.max(margin, x), window.innerWidth - width - margin),
        y: Math.min(Math.max(margin, y), window.innerHeight - height - margin),
      };
    };

    const getDraggableStyle = key => {
      const position = panelPositions.value[key];
      if (!position) return {};

      return {
        left: `${position.x}px`,
        top: `${position.y}px`,
        right: 'auto',
        bottom: 'auto',
      };
    };

    const onPanelDrag = event => {
      if (!dragState.value) return;
      const { key, offsetX, offsetY, width, height } = dragState.value;
      const next = clampPanelPosition(
        event.clientX - offsetX,
        event.clientY - offsetY,
        width,
        height
      );
      panelPositions.value = {
        ...panelPositions.value,
        [key]: next,
      };
    };

    const stopPanelDrag = () => {
      dragState.value = null;
      window.removeEventListener('pointermove', onPanelDrag);
      window.removeEventListener('pointerup', stopPanelDrag);
      window.removeEventListener('pointercancel', stopPanelDrag);
      window.removeEventListener('mousemove', onPanelDrag);
      window.removeEventListener('mouseup', stopPanelDrag);
    };

    const startPanelDrag = (event, key) => {
      if (dragState.value) return;
      if (event.button !== 0) return;
      const panel = event.currentTarget.closest('[data-vc-draggable-panel]');
      if (!panel) return;

      const rect = panel.getBoundingClientRect();
      const currentPosition = panelPositions.value[key] || {
        x: rect.left,
        y: rect.top,
      };
      const next = clampPanelPosition(
        currentPosition.x,
        currentPosition.y,
        rect.width,
        rect.height
      );

      panelPositions.value = {
        ...panelPositions.value,
        [key]: next,
      };
      dragState.value = {
        key,
        width: rect.width,
        height: rect.height,
        offsetX: event.clientX - next.x,
        offsetY: event.clientY - next.y,
      };

      event.preventDefault();
      window.addEventListener('pointermove', onPanelDrag);
      window.addEventListener('pointerup', stopPanelDrag);
      window.addEventListener('pointercancel', stopPanelDrag);
      window.addEventListener('mousemove', onPanelDrag);
      window.addEventListener('mouseup', stopPanelDrag);
    };

    // Ringtone — pre-created context to bypass browser autoplay policy
    let ringtoneCtx = null;
    let ringtoneTimer = null;
    let ringbackCtx = null;
    let ringbackTimer = null;
    let sharedAudioCtx = null;
    const initAudioCtx = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx && !sharedAudioCtx) {
          sharedAudioCtx = new Ctx();
        }
        if (sharedAudioCtx?.state === 'suspended') {
          sharedAudioCtx.resume().catch(() => {});
        }
      } catch (_e) {
        /* handled silently */
      }
    };

    // BroadcastChannel para coordinación multi-agente
    let callBroadcast = null;
    try {
      callBroadcast = new BroadcastChannel('voice_call_1980');
    } catch (_e) {
      /* handled silently */
    }

    // --- Computed ---
    const callStatusText = computed(() => {
      const map = {
        ringing: 'Llamando...',
        progress: 'Conectando...',
        confirmed: 'En llamada',
      };
      return map[callStatus.value] || callStatus.value;
    });

    const formattedDuration = computed(() => {
      const m = Math.floor(callDuration.value / 60)
        .toString()
        .padStart(2, '0');
      const s = (callDuration.value % 60).toString().padStart(2, '0');
      return `${m}:${s}`;
    });

    // --- Timer ---
    const startCallTimer = () => {
      callDuration.value = 0;
      callTimer.value = setInterval(() => {
        callDuration.value += 1;
      }, 1000);
    };

    const stopCallTimer = () => {
      if (callTimer.value) {
        clearInterval(callTimer.value);
        callTimer.value = null;
      }
      callDuration.value = 0;
    };

    const remoteNumberFromTarget = target => {
      return String(target || '')
        .replace(/^sip:/, '')
        .split('@')[0]
        .trim();
    };

    const normalizeComparableNumber = value =>
      String(value || '').replace(/[^\d]/g, '');

    const selectedConversationIdFor = remoteNumber => {
      const currentChat = store.getters.getSelectedChat;
      const senderPhone = currentChat?.meta?.sender?.phone_number;
      const selectedMatchesRemote =
        senderPhone &&
        normalizeComparableNumber(senderPhone) ===
          normalizeComparableNumber(remoteNumber);

      return selectedMatchesRemote ? currentChat.id : null;
    };

    const sessionCallSid = session => {
      return (
        session?.id ||
        session?._request?.call_id ||
        session?._request?.callId ||
        `sip-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
      );
    };

    const currentSipExtension = () => {
      if (sipCurrentExt.value) return sipCurrentExt.value;
      const sipUri = voiceSIPClient.config?.sip_uri || '';
      const uriUser = remoteNumberFromTarget(sipUri);
      if (uriUser) return uriUser;
      return sipConfigForm.value.sipUser || '';
    };

    const buildCallMeta = ({
      session,
      direction,
      remoteNumber,
      conversationId = null,
    }) => ({
      callSid: sessionCallSid(session),
      direction,
      remoteNumber: remoteNumberFromTarget(remoteNumber),
      startedAt: new Date().toISOString(),
      conversationId: conversationId || selectedConversationIdFor(remoteNumber),
    });

    const logSipCall = async ({ status, endedAt = null, duration = null }) => {
      if (!activeCallMeta.value) return;

      const payload = {
        call_sid: activeCallMeta.value.callSid,
        direction: activeCallMeta.value.direction,
        remote_number: activeCallMeta.value.remoteNumber,
        status,
        started_at: activeCallMeta.value.startedAt,
        ended_at: endedAt,
        duration,
        extension: currentSipExtension(),
        conversation_id: activeCallMeta.value.conversationId,
      };

      try {
        await voiceSipSessionAPI.logCall(payload);
      } catch (error) {
        console.warn('[Voice SIP] Call history logging failed', {
          status: error?.response?.status,
          data: error?.response?.data,
        });
      }
    };

    const finishSipCallLog = status => {
      if (!activeCallMeta.value) return;
      const endedAt = new Date().toISOString();
      const duration =
        callDuration.value ||
        Math.max(
          0,
          Math.round(
            (new Date(endedAt).getTime() -
              new Date(activeCallMeta.value.startedAt).getTime()) /
              1000
          )
        );
      logSipCall({ status, endedAt, duration });
      activeCallMeta.value = null;
    };

    const terminateSession = session => {
      const terminated = session
        ? voiceSIPClient.hangup(session)
        : voiceSIPClient.hangup();

      if (!terminated && session) {
        return voiceSIPClient.hangup();
      }

      return terminated;
    };

    // --- Ringtone (Web Audio API) ---
    const startRingtone = () => {
      try {
        stopRingtone();
        initAudioCtx();
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        // Reutilizar contexto pre-desbloqueado por gesture del usuario
        ringtoneCtx = sharedAudioCtx || new Ctx();
        const beep = () => {
          if (!ringtoneCtx || ringtoneCtx.state === 'closed') return;
          [
            [440, 0],
            [480, 0.55],
          ].forEach(([freq, delay]) => {
            const osc = ringtoneCtx.createOscillator();
            const gain = ringtoneCtx.createGain();
            osc.connect(gain);
            gain.connect(ringtoneCtx.destination);
            osc.type = 'sine';
            osc.frequency.value = freq;
            const t = ringtoneCtx.currentTime + delay;
            gain.gain.setValueAtTime(0.35, t);
            gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
            osc.start(t);
            osc.stop(t + 0.45);
          });
        };
        const play = () => {
          beep();
          ringtoneTimer = setInterval(beep, 2200);
        };
        // resume() desbloquea contextos suspendidos por autoplay policy
        if (ringtoneCtx.state === 'suspended') {
          ringtoneCtx.resume().then(play).catch(play);
        } else {
          play();
        }
      } catch (e) {
        // handled silently
      }
    };

    const stopRingtone = () => {
      if (ringtoneTimer) {
        clearInterval(ringtoneTimer);
        ringtoneTimer = null;
      }
      // No cerrar sharedAudioCtx, solo el ringtoneCtx si es diferente
      try {
        if (ringtoneCtx && ringtoneCtx !== sharedAudioCtx) ringtoneCtx.close();
      } catch (_e) {
        /* handled silently */
      }
      ringtoneCtx = null;
    };

    const startRingback = () => {
      try {
        stopRingback();
        initAudioCtx();
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;

        ringbackCtx = sharedAudioCtx || new Ctx();
        const beep = () => {
          if (!ringbackCtx || ringbackCtx.state === 'closed') return;
          [
            [425, 0],
            [425, 0.35],
          ].forEach(([freq, delay]) => {
            const osc = ringbackCtx.createOscillator();
            const gain = ringbackCtx.createGain();
            osc.connect(gain);
            gain.connect(ringbackCtx.destination);
            osc.type = 'sine';
            osc.frequency.value = freq;
            const t = ringbackCtx.currentTime + delay;
            gain.gain.setValueAtTime(0.28, t);
            gain.gain.exponentialRampToValueAtTime(0.001, t + 0.28);
            osc.start(t);
            osc.stop(t + 0.28);
          });
        };
        const play = () => {
          beep();
          ringbackTimer = setInterval(beep, 1800);
        };

        if (ringbackCtx.state === 'suspended') {
          ringbackCtx.resume().then(play).catch(play);
        } else {
          play();
        }
      } catch (_e) {
        /* handled silently */
      }
    };

    const stopRingback = () => {
      if (ringbackTimer) {
        clearInterval(ringbackTimer);
        ringbackTimer = null;
      }
      try {
        if (ringbackCtx && ringbackCtx !== sharedAudioCtx) ringbackCtx.close();
      } catch (_e) {
        /* handled silently */
      }
      ringbackCtx = null;
    };

    // --- Desktop notification ---
    const showDesktopNotification = from => {
      if (!('Notification' in window)) return;
      const show = () => {
        try {
          const notif = new Notification('📞 Llamada entrante', {
            body: `De: ${from}`,
            requireInteraction: true,
          });
          notif.addEventListener('error', () => {
            /* no-op */
          });
        } catch (_e) {
          /* handled silently */
        }
      };
      if (Notification.permission === 'granted') {
        show();
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(p => {
          if (p === 'granted') show();
        });
      }
    };

    // --- Audio devices ---
    const loadAudioDevices = async () => {
      try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        audioInputDevices.value = devs.filter(d => d.kind === 'audioinput');
        audioOutputDevices.value = devs.filter(d => d.kind === 'audiooutput');
      } catch (e) {
        // handled silently
      }
    };

    const changeOutputDevice = async deviceId => {
      if (
        !remoteAudio.value ||
        typeof remoteAudio.value.setSinkId !== 'function'
      )
        return;
      try {
        await remoteAudio.value.setSinkId(deviceId);
      } catch (e) {
        // handled silently
      }
    };

    const changeInputDevice = async deviceId => {
      if (!activeSession.value) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: deviceId } },
          video: false,
        });
        const track = stream.getAudioTracks()[0];
        const pc = activeSession.value.connection;
        if (pc) {
          const sender = pc
            .getSenders()
            .find(s => s.track && s.track.kind === 'audio');
          if (sender) {
            await sender.replaceTrack(track);
          }
        }
      } catch (e) {
        // handled silently
      }
    };

    const toggleDevicePanel = () => {
      showDevicePanel.value = !showDevicePanel.value;
      showTransferPanel.value = false;
      if (showDevicePanel.value) loadAudioDevices();
    };

    const toggleTransfer = () => {
      showTransferPanel.value = !showTransferPanel.value;
      showDevicePanel.value = false;
    };

    // --- Cleanup (moved before first usage to avoid no-use-before-define) ---
    const cleanup = () => {
      stopCallTimer();
      stopRingtone();
      stopRingback();
      showActiveCall.value = false;
      showTransferPanel.value = false;
      showDevicePanel.value = false;
      activeSession.value = null;
      activeCallNumber.value = '';
      callStatus.value = '';
      isHangingUp.value = false;
      isMuted.value = false;
      isOnHold.value = false;
      transferTarget.value = '';
    };

    // --- Transfer ---
    const doTransfer = () => {
      if (!activeSession.value || !transferTarget.value) return;
      const domain =
        voiceSIPClient.config?.domain ||
        sipConfigForm.value.domain ||
        'demo.1980tic.cloud';
      try {
        activeSession.value.refer(`sip:${transferTarget.value}@${domain}`);
        cleanup();
      } catch (e) {
        // handled silently
      }
    };

    // --- Remote audio attach ---
    const attachRemoteAudio = session => {
      session.on('peerconnection', e => {
        const pc = e.peerconnection;
        pc.ontrack = event => {
          if (event.streams && event.streams[0] && remoteAudio.value) {
            remoteAudio.value.srcObject = event.streams[0];
          }
        };
        pc.addEventListener('addstream', event => {
          if (remoteAudio.value) {
            remoteAudio.value.srcObject = event.stream;
          }
        });
      });
    };

    // --- Incoming call ---
    const clearIncomingCall = (session = null) => {
      if (session && incomingSession.value && incomingSession.value !== session)
        return;
      stopRingtone();
      showIncomingCall.value = false;
      incomingSession.value = null;
      incomingCallFrom.value = '';
    };

    const handleIncomingCall = session => {
      const from = session.remote_identity.uri.user;
      incomingSession.value = session;
      incomingCallFrom.value = from;
      showIncomingCall.value = true;
      session.on('accepted', () => stopRingtone());
      session.on('ended', () => clearIncomingCall(session));
      session.on('failed', () => clearIncomingCall(session));
      startRingtone();
      showDesktopNotification(from);
    };

    const acceptCall = () => {
      if (!incomingSession.value) return;
      stopRingtone();
      // Notificar a otros agentes (otras pestañas) que la llamada fue contestada
      try {
        if (callBroadcast) callBroadcast.postMessage({ type: 'call-answered' });
      } catch (_e) {
        /* handled silently */
      }
      const session = incomingSession.value;

      session.on('confirmed', () => {
        callStatus.value = 'confirmed';
        startCallTimer();
      });
      session.on('ended', () => {
        finishSipCallLog('completed');
        cleanup();
      });
      session.on('failed', () => {
        finishSipCallLog('failed');
        cleanup();
      });

      attachRemoteAudio(session);
      session.answer({ mediaConstraints: { audio: true, video: false } });

      activeSession.value = session;
      activeCallNumber.value = incomingCallFrom.value;
      activeCallMeta.value = buildCallMeta({
        session,
        direction: 'incoming',
        remoteNumber: incomingCallFrom.value,
      });
      callStatus.value = 'progress';
      showIncomingCall.value = false;
      showActiveCall.value = true;
      incomingSession.value = null;
      logSipCall({ status: 'in_progress' });
    };

    const rejectCall = () => {
      const session = incomingSession.value;
      stopRingtone();
      try {
        if (callBroadcast) callBroadcast.postMessage({ type: 'call-rejected' });
      } catch (_e) {
        /* handled silently */
      }
      if (session) {
        session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
      }
      clearIncomingCall(session);
    };

    const handleSipActiveElsewhere = event => {
      sipRegistered.value = false;
      sipActiveElsewhere.value = event.detail?.activeSession || null;
      showSipActiveElsewhere.value = true;
    };

    const reconnectSipHere = async () => {
      if (!window.voiceSIPTakeover) return;
      isTakingOverSip.value = true;
      try {
        await window.voiceSIPTakeover();
        showSipActiveElsewhere.value = false;
      } finally {
        isTakingOverSip.value = false;
      }
    };

    const logoutCurrentDevice = async () => {
      try {
        voiceSIPClient.disconnect();
        await Auth.logout();
      } finally {
        window.location.href = '/app/login';
      }
    };

    // --- Controls ---
    const toggleMute = () => {
      if (!activeSession.value) return;
      isMuted.value ? activeSession.value.unmute() : activeSession.value.mute();
      isMuted.value = !isMuted.value;
    };

    const toggleHold = () => {
      if (!activeSession.value) return;
      isOnHold.value
        ? activeSession.value.unhold()
        : activeSession.value.hold();
      isOnHold.value = !isOnHold.value;
    };

    const hangUp = () => {
      if (isHangingUp.value) return;
      isHangingUp.value = true;
      const session = activeSession.value || voiceSIPClient.currentSession;
      const finalStatus =
        callStatus.value === 'confirmed' ? 'completed' : 'cancelled';
      finishSipCallLog(finalStatus);
      stopRingtone();
      stopRingback();
      terminateSession(session);
      cleanup();
    };

    const normalizeCallTarget = number => {
      const target = String(number || '').trim();
      if (!target || target.startsWith('sip:') || target.includes('@')) {
        return target;
      }

      const domain =
        voiceSIPClient.config?.domain ||
        sipConfigForm.value.domain ||
        'demo.1980tic.cloud';
      return `sip:${target.replace(/^tel:/, '')}@${domain}`;
    };

    // --- Outgoing call ---
    const makeCall = (phoneNumber, options = {}) => {
      const normalizedTarget = normalizeCallTarget(phoneNumber);
      console.info(`[Voice SIP] Outgoing call target: ${normalizedTarget}`, {
        input: phoneNumber,
        target: normalizedTarget,
      });
      const session = voiceSIPClient.makeCall(normalizedTarget);
      if (!session) {
        return null;
      }

      showDialer.value = false;
      activeSession.value = session;
      activeCallNumber.value = phoneNumber;
      activeCallMeta.value = buildCallMeta({
        session,
        direction: 'outbound',
        remoteNumber: phoneNumber,
        conversationId: options.conversationId,
      });
      callStatus.value = 'ringing';
      showActiveCall.value = true;
      startRingback();
      logSipCall({ status: 'ringing' });

      session.on('progress', event => {
        console.info('[Voice SIP] Modal progress', {
          cause: event?.cause,
          status: event?.response?.status_code || event?.message?.status_code,
          reason:
            event?.response?.reason_phrase || event?.message?.reason_phrase,
        });
        callStatus.value = 'progress';
        startRingback();
      });
      session.on('accepted', () => {
        stopRingback();
      });
      session.on('confirmed', () => {
        stopRingback();
        callStatus.value = 'confirmed';
        startCallTimer();
        logSipCall({ status: 'in_progress' });
      });
      session.on('ended', () => {
        stopRingback();
        finishSipCallLog('completed');
        cleanup();
      });
      session.on('failed', event => {
        console.error('[Voice SIP] Modal failed', {
          cause: event?.cause,
          status: event?.response?.status_code || event?.message?.status_code,
          reason:
            event?.response?.reason_phrase || event?.message?.reason_phrase,
        });
        stopRingback();
        finishSipCallLog('failed');
        cleanup();
      });

      attachRemoteAudio(session);
      return session;
    };

    const openDialer = (number = '') => {
      dialerError.value = '';
      if (number) dialerNumber.value = String(number);
      showDevicePanel.value = false;
      showTransferPanel.value = false;
      showDialer.value = true;
    };

    const closeDialer = () => {
      showDialer.value = false;
      dialerError.value = '';
    };

    const appendDialerKey = key => {
      dialerError.value = '';
      dialerNumber.value = `${dialerNumber.value}${key}`;
    };

    const backspaceDialer = () => {
      dialerNumber.value = dialerNumber.value.slice(0, -1);
    };

    const callFromDialer = (number = dialerNumber.value) => {
      const target = String(number || '').trim();
      if (!target) {
        dialerError.value = 'Ingresa un numero o extension';
        return;
      }
      if (!sipRegistered.value) {
        dialerError.value = sipConfigured.value
          ? 'El telefono SIP aun no esta registrado'
          : 'Configura tu telefono SIP antes de llamar';
        return;
      }
      const session = makeCall(target);
      if (!session) {
        dialerError.value = 'No se pudo iniciar la llamada';
      }
    };

    // Exponer globalmente
    window.voiceCallModals = { makeCall, openDialer };

    const _openSipConfigHandler = () => openSipConfig();
    const _openDialerHandler = e => openDialer(e.detail?.number);
    const _incomingCallHandler = e => handleIncomingCall(e.detail.session);
    const _incomingCallEndedHandler = () => clearIncomingCall();
    const _sipNotConfiguredHandler = () => {
      sipConfigured.value = false;
      sipRegistered.value = false;
    };
    const _sipRegisteredHandler = e => {
      sipRegistered.value = true;
      sipConfigured.value = true;
      sipRegError.value = '';
      sipCurrentExt.value = e.detail?.userInfo?.extension || '';
    };
    const _sipRegFailedHandler = e => {
      sipRegistered.value = false;
      const code = e.detail?.status || '';
      const cause = e.detail?.cause || '';
      sipRegError.value = `Error de registro SIP${code ? ` (${code})` : ''}${cause ? ': ' + cause : ''}`;
    };

    onMounted(() => {
      refreshSipConfigState();
      if (voiceSIPClient.isRegistered) {
        sipRegistered.value = true;
      }

      window.addEventListener('voice:openSipConfig', _openSipConfigHandler);
      window.addEventListener('voice:openDialer', _openDialerHandler);
      window.addEventListener('voice:incomingCall', _incomingCallHandler);
      window.addEventListener(
        'voice:incomingCallEnded',
        _incomingCallEndedHandler
      );
      window.addEventListener(
        'voice:sipNotConfigured',
        _sipNotConfiguredHandler
      );
      window.addEventListener('sip:registered', _sipRegisteredHandler);
      window.addEventListener('sip:registrationFailed', _sipRegFailedHandler);
      window.addEventListener(
        'voice:sipActiveElsewhere',
        handleSipActiveElsewhere
      );

      // Pre-desbloquear AudioContext en primer gesto del usuario
      document.addEventListener('click', initAudioCtx);
      document.addEventListener('keydown', initAudioCtx);

      // BroadcastChannel: call-answered + presencia SIP
      if (callBroadcast) {
        callBroadcast.onmessage = e => {
          const { type, userId, name, extension, timestamp } = e.data;

          if (type === 'call-answered' && showIncomingCall.value) {
            clearIncomingCall();
          }

          if (type === 'call-rejected' && showIncomingCall.value) {
            clearIncomingCall();
          }

          if (
            type === 'sip-online' ||
            type === 'sip-im-online' ||
            type === 'sip-heartbeat'
          ) {
            if (userId && name && extension)
              updatePresence(userId, name, extension, timestamp);
          }

          if (type === 'sip-offline' && userId) {
            removePresence(userId);
          }
        };

        // Solicitar lista de agentes conectados
        try {
          callBroadcast.postMessage({ type: 'sip-who-is-online' });
        } catch (_e) {
          /* handled silently */
        }
      }
    });

    onBeforeUnmount(() => {
      cleanup();
      stopPanelDrag();
      window.removeEventListener('voice:openSipConfig', _openSipConfigHandler);
      window.removeEventListener('voice:openDialer', _openDialerHandler);
      window.removeEventListener('voice:incomingCall', _incomingCallHandler);
      window.removeEventListener(
        'voice:incomingCallEnded',
        _incomingCallEndedHandler
      );
      window.removeEventListener(
        'voice:sipNotConfigured',
        _sipNotConfiguredHandler
      );
      window.removeEventListener('sip:registered', _sipRegisteredHandler);
      window.removeEventListener(
        'sip:registrationFailed',
        _sipRegFailedHandler
      );
      window.removeEventListener(
        'voice:sipActiveElsewhere',
        handleSipActiveElsewhere
      );
      document.removeEventListener('click', initAudioCtx);
      document.removeEventListener('keydown', initAudioCtx);
      try {
        stopRingback();
        if (sharedAudioCtx) sharedAudioCtx.close();
      } catch (_e) {
        /* handled silently */
      }
      try {
        if (callBroadcast) callBroadcast.close();
      } catch (_e) {
        /* handled silently */
      }
    });

    return {
      showIncomingCall,
      showActiveCall,
      incomingCallFrom,
      activeCallNumber,
      callStatusText,
      formattedDuration,
      callDuration,
      isMuted,
      isHangingUp,
      isOnHold,
      remoteAudio,
      showDialer,
      dialerNumber,
      dialerError,
      dialPadKeys,
      showTransferPanel,
      transferTarget,
      connectedAgents,
      showDevicePanel,
      audioInputDevices,
      audioOutputDevices,
      selectedInputId,
      selectedOutputId,
      acceptCall,
      rejectCall,
      toggleMute,
      toggleHold,
      hangUp,
      makeCall,
      openDialer,
      closeDialer,
      appendDialerKey,
      backspaceDialer,
      callFromDialer,
      doTransfer,
      toggleTransfer,
      changeOutputDevice,
      changeInputDevice,
      toggleDevicePanel,
      showSipConfig,
      showSipAdvanced,
      sipConfigForm,
      saveSipConfig,
      openSipConfig,
      sipRegError,
      sipConfigured,
      sipRegistered,
      sipCurrentExt,
      showSipActiveElsewhere,
      sipActiveElsewhere,
      isTakingOverSip,
      reconnectSipHere,
      logoutCurrentDevice,
      getDraggableStyle,
      startPanelDrag,
    };
  },
};
</script>

<template>
  <!-- Popup llamada entrante: no bloquea la pantalla -->
  <Teleport to="body">
    <Transition name="vc-pop">
      <div
        v-if="showIncomingCall"
        class="vc-incoming-float vc-draggable-panel"
        data-vc-draggable-panel
        :style="getDraggableStyle('incoming')"
      >
        <div
          class="vc-incoming-head vc-drag-handle"
          @pointerdown="startPanelDrag($event, 'incoming')"
          @mousedown="startPanelDrag($event, 'incoming')"
        >
          <div class="vc-avatar vc-avatar--pulse">
            <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
              <path
                d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
              />
            </svg>
          </div>
          <div class="vc-incoming-copy">
            <p class="vc-label">Llamada entrante</p>
            <h2 class="vc-number">{{ incomingCallFrom }}</h2>
          </div>
        </div>
        <div class="vc-row vc-row--gap">
          <button
            class="vc-action-btn vc-action-btn--reject"
            @click="rejectCall"
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              width="18"
              height="18"
              style="transform: rotate(135deg)"
            >
              <path
                d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
              />
            </svg>
            Rechazar
          </button>
          <button
            class="vc-action-btn vc-action-btn--accept"
            @click="acceptCall"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path
                d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
              />
            </svg>
            Aceptar
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Modal configuración SIP por usuario -->
  <Teleport to="body">
    <div
      v-if="showSipConfig"
      class="vc-sip-backdrop"
      @click.self="showSipConfig = false"
    >
      <div
        class="vc-sip-config vc-draggable-panel"
        data-vc-draggable-panel
        :style="getDraggableStyle('sipConfig')"
      >
        <p
          class="vc-sip-config-title vc-drag-handle"
          @pointerdown="startPanelDrag($event, 'sipConfig')"
          @mousedown="startPanelDrag($event, 'sipConfig')"
        >
          Conectar teléfono SIP
        </p>
        <p class="vc-sip-config-sub">
          Ingresa tu número y contraseña SIP — tu administrador te los
          proporciona
        </p>

        <label class="vc-panel-label">Número / Usuario SIP</label>
        <input
          v-model="sipConfigForm.sipUser"
          class="vc-input"
          placeholder="ej: 3336025219"
          type="tel"
          autocomplete="username"
        />

        <label class="vc-panel-label">Contraseña SIP</label>
        <input
          v-model="sipConfigForm.password"
          class="vc-input"
          type="password"
          placeholder="••••••"
          autocomplete="current-password"
        />

        <!-- Ajustes avanzados (ocultos por defecto) -->
        <button
          type="button"
          class="vc-sip-advanced-toggle"
          @click="showSipAdvanced = !showSipAdvanced"
        >
          {{ showSipAdvanced ? '▲' : '▼' }} Ajustes avanzados (servidor /
          dominio)
        </button>
        <template v-if="showSipAdvanced">
          <label class="vc-panel-label">Servidor WebSocket</label>
          <input
            v-model="sipConfigForm.websocket_server"
            class="vc-input"
            placeholder="wss://tu-pbx.com:443/ws"
          />
          <label class="vc-panel-label">Dominio SIP</label>
          <input
            v-model="sipConfigForm.domain"
            class="vc-input"
            placeholder="tu-pbx.com"
          />
        </template>

        <!-- Error de registro -->
        <p v-if="sipRegError" class="vc-sip-error">{{ sipRegError }}</p>

        <div class="vc-row vc-row--gap" style="margin-top: 1rem">
          <button
            class="vc-panel-btn vc-panel-btn--cancel"
            @click="showSipConfig = false"
          >
            Cancelar
          </button>
          <button
            class="vc-panel-btn"
            :disabled="!sipConfigForm.sipUser || !sipConfigForm.password"
            @click="saveSipConfig"
          >
            Conectar SIP
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Indicador SIP persistente — siempre visible -->
  <Teleport to="body">
    <div v-if="showSipActiveElsewhere" class="vc-sip-backdrop">
      <div
        class="vc-sip-config vc-draggable-panel"
        data-vc-draggable-panel
        :style="getDraggableStyle('sipElsewhere')"
      >
        <p
          class="vc-sip-config-title vc-drag-handle"
          @pointerdown="startPanelDrag($event, 'sipElsewhere')"
          @mousedown="startPanelDrag($event, 'sipElsewhere')"
        >
          Cuenta activa en otro dispositivo
        </p>
        <p class="vc-sip-config-sub">
          Esta extension SIP ya esta conectada
          <span v-if="sipActiveElsewhere?.device_name">
            en {{ sipActiveElsewhere.device_name }}
          </span>
          . Solo puede estar activa en un dispositivo. Transfiere el dispositivo
          para usarla aqui o cierra esta sesion.
        </p>

        <div class="vc-row vc-row--gap" style="margin-top: 1rem">
          <button
            class="vc-panel-btn vc-panel-btn--cancel"
            @click="logoutCurrentDevice"
          >
            Cerrar sesion
          </button>
          <button
            class="vc-panel-btn"
            :disabled="isTakingOverSip"
            @click="reconnectSipHere"
          >
            {{
              isTakingOverSip ? 'Transfiriendo...' : 'Transferir dispositivo'
            }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div
      v-if="false"
      class="vc-sip-pill"
      :class="[
        sipRegistered && 'vc-sip-pill--ok',
        sipConfigured && !sipRegistered && 'vc-sip-pill--pending',
      ]"
      title="Configurar teléfono SIP"
      @click="openSipConfig"
    >
      <span class="vc-sip-pill-dot" />
      <span v-if="sipRegistered">SIP {{ sipCurrentExt }}</span>
      <span v-else-if="sipConfigured">Conectando...</span>
      <span v-else>Conectar SIP</span>
    </div>
  </Teleport>

  <!-- Panel flotante llamada activa (sin backdrop — el usuario puede seguir trabajando) -->
  <Teleport to="body">
    <Transition name="vc-pop">
      <div
        v-if="showDialer && !showActiveCall"
        class="vc-dialer vc-draggable-panel"
        data-vc-draggable-panel
        :style="getDraggableStyle('dialer')"
      >
        <div
          class="vc-dialer-header vc-drag-handle"
          @pointerdown="startPanelDrag($event, 'dialer')"
          @mousedown="startPanelDrag($event, 'dialer')"
        >
          <div>
            <p class="vc-status-text">Telefono SIP</p>
            <h2 class="vc-dialer-title">Nueva llamada</h2>
          </div>
          <button class="vc-icon-btn" title="Cerrar" @click="closeDialer">
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path
                d="M18.3 5.71 12 12l6.3 6.29-1.41 1.41L10.59 13.41 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.3-6.29z"
              />
            </svg>
          </button>
        </div>

        <div
          class="vc-dialer-state"
          :class="[sipRegistered && 'vc-dialer-state--ok']"
        >
          <span class="vc-sip-pill-dot" />
          <span v-if="sipRegistered">SIP conectado {{ sipCurrentExt }}</span>
          <span v-else-if="sipConfigured">SIP conectando...</span>
          <span v-else>SIP sin configurar</span>
          <button
            v-if="!sipRegistered"
            class="vc-link-btn"
            @click="openSipConfig"
          >
            Configurar
          </button>
        </div>

        <div class="vc-dialer-input-row">
          <input
            v-model="dialerNumber"
            class="vc-dialer-input"
            placeholder="Numero o extension"
            type="tel"
            autofocus
            @keyup.enter="callFromDialer()"
          />
          <button
            class="vc-icon-btn"
            title="Borrar"
            :disabled="!dialerNumber"
            @click="backspaceDialer"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path
                d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.12c.36.53.9.88 1.59.88h15V3zm-3 12.59L17.59 17 14 13.41 10.41 17 9 15.59 12.59 12 9 8.41 10.41 7 14 10.59 17.59 7 19 8.41 15.41 12 19 15.59z"
              />
            </svg>
          </button>
        </div>

        <div class="vc-dialpad">
          <button
            v-for="key in dialPadKeys"
            :key="key"
            class="vc-dial-key"
            @click="appendDialerKey(key)"
          >
            {{ key }}
          </button>
        </div>

        <div v-if="connectedAgents.length" class="vc-dialer-agents">
          <p class="vc-panel-title">Agentes conectados</p>
          <div class="vc-areas">
            <button
              v-for="agent in connectedAgents"
              :key="agent.extension"
              class="vc-area-btn"
              @click="callFromDialer(agent.extension)"
            >
              {{ agent.name }}
              <span class="vc-area-ext">ext. {{ agent.extension }}</span>
            </button>
          </div>
        </div>

        <p v-if="dialerError" class="vc-sip-error">{{ dialerError }}</p>

        <button
          class="vc-dialer-call"
          :disabled="!dialerNumber"
          @click="callFromDialer()"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path
              d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
            />
          </svg>
          Llamar
        </button>
      </div>
    </Transition>
  </Teleport>

  <!-- Panel flotante llamada activa (sin backdrop, el usuario puede seguir trabajando) -->
  <Teleport to="body">
    <Transition name="vc-slide">
      <div
        v-if="showActiveCall"
        class="vc-float vc-draggable-panel"
        data-vc-draggable-panel
        :style="getDraggableStyle('active')"
      >
        <audio ref="remoteAudio" autoplay />

        <!-- Header: avatar + info + colgar -->
        <div
          class="vc-float-header vc-drag-handle"
          @pointerdown="startPanelDrag($event, 'active')"
          @mousedown="startPanelDrag($event, 'active')"
        >
          <div class="vc-float-avatar">
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path
                d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
              />
            </svg>
          </div>
          <div class="vc-float-info">
            <p class="vc-status-text">{{ callStatusText }}</p>
            <h2 class="vc-number">{{ activeCallNumber }}</h2>
            <p v-if="callDuration > 0" class="vc-duration">
              {{ formattedDuration }}
            </p>
          </div>
          <button
            class="vc-hangup-btn"
            title="Colgar"
            :disabled="isHangingUp"
            @click="hangUp"
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              width="20"
              height="20"
              style="transform: rotate(135deg)"
            >
              <path
                d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
              />
            </svg>
          </button>
        </div>

        <!-- Fila de controles -->
        <div class="vc-controls">
          <!-- Silenciar -->
          <button
            class="vc-ctrl"
            :class="[isMuted && 'vc-ctrl--active']"
            :title="isMuted ? 'Activar mic' : 'Silenciar'"
            @click="toggleMute"
          >
            <svg
              v-if="!isMuted"
              viewBox="0 0 24 24"
              fill="currentColor"
              width="20"
              height="20"
            >
              <path
                d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1 1.93V19H9v2h6v-2h-2v-2.07A7.003 7.003 0 0 0 19 11h-2a5 5 0 0 1-10 0H5a7.003 7.003 0 0 0 6 6.93z"
              />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              fill="currentColor"
              width="20"
              height="20"
            >
              <path
                d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"
              />
            </svg>
            <span>{{ isMuted ? 'Activar' : 'Silenciar' }}</span>
          </button>

          <!-- Hold -->
          <button
            class="vc-ctrl"
            :class="[isOnHold && 'vc-ctrl--active']"
            :title="isOnHold ? 'Reanudar' : 'Pausar'"
            @click="toggleHold"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
            </svg>
            <span>{{ isOnHold ? 'Reanudar' : 'Pausar' }}</span>
          </button>

          <!-- Transferir -->
          <button
            class="vc-ctrl"
            :class="[showTransferPanel && 'vc-ctrl--active']"
            title="Transferir"
            @click="toggleTransfer"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path
                d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"
              />
            </svg>
            <span>Transferir</span>
          </button>

          <!-- Dispositivo -->
          <button
            class="vc-ctrl"
            :class="[showDevicePanel && 'vc-ctrl--active']"
            title="Dispositivos"
            @click="toggleDevicePanel"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path
                d="M12 1c-4.97 0-9 4.03-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7c0-4.97-4.03-9-9-9z"
              />
            </svg>
            <span>Dispositivo</span>
          </button>
        </div>

        <!-- Panel transferencia -->
        <div v-if="showTransferPanel" class="vc-panel">
          <p class="vc-panel-title">Transferir a agente</p>
          <!-- Agentes con SIP conectado -->
          <div v-if="connectedAgents.length" class="vc-areas">
            <button
              v-for="agent in connectedAgents"
              :key="agent.extension"
              class="vc-area-btn"
              @click="
                transferTarget = agent.extension;
                doTransfer();
              "
            >
              {{ agent.name }}
              <span class="vc-area-ext">ext. {{ agent.extension }}</span>
            </button>
          </div>
          <p v-else class="vc-panel-empty">No hay agentes con SIP conectado</p>
          <!-- Input manual -->
          <div class="vc-row vc-row--gap">
            <input
              v-model="transferTarget"
              class="vc-input"
              placeholder="Número o extensión"
              type="tel"
              @keyup.enter="doTransfer"
            />
            <button
              class="vc-panel-btn"
              :disabled="!transferTarget"
              @click="doTransfer"
            >
              Transferir
            </button>
          </div>
        </div>

        <!-- Panel dispositivos -->
        <div v-if="showDevicePanel" class="vc-panel">
          <p class="vc-panel-title">Dispositivos de audio</p>
          <label class="vc-panel-label">Micrófono</label>
          <select
            v-model="selectedInputId"
            class="vc-select"
            @change="changeInputDevice(selectedInputId)"
          >
            <option
              v-for="(d, i) in audioInputDevices"
              :key="d.deviceId"
              :value="d.deviceId"
            >
              {{ d.label || 'Micrófono ' + (i + 1) }}
            </option>
          </select>
          <label class="vc-panel-label">Altavoz / Auricular</label>
          <select
            v-model="selectedOutputId"
            class="vc-select"
            @change="changeOutputDevice(selectedOutputId)"
          >
            <option
              v-for="(d, i) in audioOutputDevices"
              :key="d.deviceId"
              :value="d.deviceId"
            >
              {{ d.label || 'Altavoz ' + (i + 1) }}
            </option>
          </select>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
// Color tokens from Chatwoot design system (_next-colors.scss)
// Usage: rgb(var(--blue-9)) adapts automatically to dark/light mode

.vc-incoming {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1.5rem;
  min-width: 300px;
}

.vc-incoming-float,
.vc-float,
.vc-dialer {
  position: fixed;
  right: 1.25rem;
  bottom: 1.25rem;
  max-width: calc(100vw - 2rem);
  border: 1px solid rgba(38, 217, 247, 0.3);
  border-radius: 1.15rem;
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.1), transparent 42%),
    linear-gradient(135deg, rgba(6, 4, 14, 0.92), rgba(24, 84, 101, 0.82));
  box-shadow:
    0 1.5rem 4rem rgba(0, 0, 0, 0.42),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  color: #f8fafc;
  backdrop-filter: blur(24px) saturate(150%);
}

.vc-drag-handle {
  cursor: grab;
  touch-action: none;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
}

.vc-incoming-float {
  z-index: 9995;
  width: 340px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.95rem;
}

.vc-incoming-head {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.vc-incoming-head .vc-avatar {
  width: 3.25rem;
  height: 3.25rem;
  margin-bottom: 0;
  flex-shrink: 0;
}

.vc-incoming-copy {
  min-width: 0;

  .vc-label,
  .vc-number {
    margin: 0;
  }

  .vc-number {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// Panel flotante — esquina inferior izquierda (encima del perfil de usuario)
.vc-float {
  position: fixed;
  right: 1.25rem;
  bottom: 1.25rem;
  z-index: 9990;
  width: 300px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.vc-dialer {
  position: fixed;
  right: 1.25rem;
  bottom: 1.25rem;
  z-index: 9985;
  width: 320px;
  max-width: calc(100vw - 2rem);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.vc-dialer-header,
.vc-dialer-input-row,
.vc-dialer-state,
.vc-dialer-call {
  display: flex;
  align-items: center;
}

.vc-dialer-header {
  justify-content: space-between;
  gap: 0.75rem;
}

.vc-dialer-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
}

.vc-dialer-state {
  gap: 0.4rem;
  min-height: 1.875rem;
  padding: 0.35rem 0.5rem;
  border-radius: 0.7rem;
  border: 1px solid rgba(148, 234, 255, 0.18);
  background: rgba(255, 255, 255, 0.055);
  color: rgba(226, 232, 240, 0.82);
  font-size: 0.75rem;

  &--ok {
    border-color: rgba(47, 255, 210, 0.32);
    background: rgba(47, 255, 210, 0.12);
    color: #2fffd2;
    .vc-sip-pill-dot {
      background: #2fffd2;
    }
  }
}

.vc-link-btn {
  margin-left: auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: #26d9f7;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}

.vc-dialer-input-row {
  gap: 0.5rem;
}

.vc-dialer-input {
  flex: 1;
  min-width: 0;
  height: 2.75rem;
  padding: 0 0.75rem;
  border: 1px solid rgba(148, 234, 255, 0.22);
  border-radius: 0.7rem;
  background: rgba(0, 0, 0, 0.22);
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  outline: none;
  &:focus {
    border-color: rgba(38, 217, 247, 0.7);
    box-shadow: 0 0 0 2px rgba(38, 217, 247, 0.14);
  }
}

.vc-icon-btn {
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  border: 1px solid rgba(148, 234, 255, 0.18);
  border-radius: 0.7rem;
  background: rgba(255, 255, 255, 0.055);
  color: rgba(226, 232, 240, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: rgba(38, 217, 247, 0.14);
    color: #fff;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.vc-dialpad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.45rem;
}

.vc-dial-key {
  min-height: 2.5rem;
  border: 1px solid rgba(148, 234, 255, 0.16);
  border-radius: 0.7rem;
  background: rgba(255, 255, 255, 0.045);
  color: #fff;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    border-color: rgba(38, 217, 247, 0.34);
    background: rgba(38, 217, 247, 0.14);
  }
}

.vc-dialer-agents {
  padding-top: 0.25rem;
}

.vc-dialer-call {
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  min-height: 2.75rem;
  border: 0;
  border-radius: 0.8rem;
  background: linear-gradient(135deg, #2fffd2, #26d9f7);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) {
    opacity: 0.88;
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.vc-float-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.vc-float-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b2ac9, #26d9f7);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.vc-float-info {
  flex: 1;
  min-width: 0;
  .vc-number {
    font-size: 1rem;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .vc-duration {
    font-size: 0.8rem;
    margin: 0;
  }
  .vc-status-text {
    margin: 0 0 0.1rem;
  }
}

// Transición slide desde la izquierda
.vc-slide-enter-active,
.vc-slide-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.25s ease;
}
.vc-slide-enter-from,
.vc-slide-leave-to {
  opacity: 0;
  transform: translateY(0.75rem) scale(0.98);
}

.vc-pop-enter-active,
.vc-pop-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.18s ease;
}

.vc-pop-enter-from,
.vc-pop-leave-to {
  opacity: 0;
  transform: translateY(0.5rem) scale(0.98);
}

// Avatar pulsante con color brand
.vc-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b2ac9, #26d9f7);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.vc-avatar--pulse {
  animation: vcPulse 1.5s ease-in-out infinite;
}

@keyframes vcPulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(38, 217, 247, 0.5);
  }
  50% {
    box-shadow: 0 0 0 14px rgba(38, 217, 247, 0);
  }
}

// Texto secundario
.vc-label {
  font-size: 0.875rem;
  color: rgba(226, 232, 240, 0.7);
  margin-bottom: 0.25rem;
}

// Número — blanco en oscuro, oscuro en claro (--slate-12 es adaptativo)
.vc-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.25rem;
}

// Duración con color brand
.vc-duration {
  font-size: 1rem;
  font-family: monospace;
  color: #2fffd2;
  margin-bottom: 1rem;
}

// Estado ("LLAMANDO..." etc.)
.vc-status-text {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(226, 232, 240, 0.62);
  margin-bottom: 0.25rem;
}

.vc-row {
  display: flex;
  width: 100%;
}

.vc-row--gap {
  gap: 0.75rem;
}

// Botones Aceptar / Rechazar
.vc-action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 0.8rem;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  transition:
    opacity 0.15s,
    transform 0.1s;
  &:hover {
    opacity: 0.88;
  }
  &:active {
    transform: scale(0.97);
  }
}

// Rechazar → rojo ruby
.vc-action-btn--reject {
  background: linear-gradient(135deg, #ef4444, #ff4d8d);
}

// Aceptar → brand woot purple
.vc-action-btn--accept {
  background: linear-gradient(135deg, #18b981, #2fffd2);
}

// Botón colgar — compacto en el header del panel flotante
.vc-hangup-btn {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, #ef4444, #ff4d8d);
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    opacity 0.15s,
    transform 0.1s;
  box-shadow: 0 3px 14px rgba(255, 77, 141, 0.34);
  &:hover {
    opacity: 0.88;
  }
  &:active {
    transform: scale(0.95);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
    transform: none;
  }
}

// Fila de controles
.vc-controls {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
}

// Botón de control individual
.vc-ctrl {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.6rem 0.75rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(148, 234, 255, 0.16);
  background: rgba(255, 255, 255, 0.055);
  color: rgba(226, 232, 240, 0.8);
  cursor: pointer;
  font-size: 0.65rem;
  font-weight: 500;
  min-width: 66px;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
  &:hover {
    background: rgba(38, 217, 247, 0.14);
    color: #fff;
  }

  // Estado activo → color brand
  &--active {
    background: rgba(38, 217, 247, 0.18);
    border-color: rgba(38, 217, 247, 0.42);
    color: #fff;
    &:hover {
      opacity: 0.88;
    }
  }
}

// Indicador SIP persistente (siempre visible, esquina inferior izquierda)
.vc-sip-pill {
  position: fixed;
  bottom: 0.625rem;
  left: 0.625rem;
  z-index: 9000;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  border: 1px solid rgb(var(--slate-5));
  background: rgb(var(--slate-2));
  color: rgb(var(--slate-9));
  font-size: 0.68rem;
  font-weight: 500;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);

  &:hover {
    background: rgb(var(--slate-4));
    color: rgb(var(--slate-12));
  }

  // SIP conectado
  &--ok {
    border-color: #22c55e33;
    background: #16a34a18;
    color: #16a34a;
    .vc-sip-pill-dot {
      background: #22c55e;
    }
  }

  // SIP configurado pero aún conectando
  &--pending {
    border-color: rgb(var(--amber-6));
    color: rgb(var(--amber-9));
    .vc-sip-pill-dot {
      background: rgb(var(--amber-9));
    }
  }
}

.vc-sip-pill-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(148, 163, 184, 0.9);
  flex-shrink: 0;
}

// Mensaje vacío en panel
.vc-panel-empty {
  font-size: 0.75rem;
  color: rgba(226, 232, 240, 0.62);
  text-align: center;
  margin-bottom: 0.625rem;
}

// Botón cancelar en panel
.vc-panel-btn--cancel {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(226, 232, 240, 0.85);
  &:hover {
    background: rgba(255, 255, 255, 0.13);
  }
}

// Backdrop del modal de config SIP
.vc-sip-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(6, 4, 14, 0.54);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
}

// Tarjeta de configuración SIP
.vc-sip-config {
  position: fixed;
  background: #102735;
  border: 1px solid rgba(38, 217, 247, 0.3);
  border-radius: 1.15rem;
  padding: 1.5rem;
  width: 340px;
  max-width: 90vw;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  box-shadow:
    0 1.5rem 4rem rgba(0, 0, 0, 0.42),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  color: #f8fafc;
  backdrop-filter: blur(24px) saturate(150%);
}

.vc-sip-config-title {
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.1rem;
}

.vc-sip-config-sub {
  font-size: 0.75rem;
  color: rgba(226, 232, 240, 0.66);
  margin-bottom: 0.5rem;
}

.vc-sip-advanced-toggle {
  font-size: 0.7rem;
  color: rgba(38, 217, 247, 0.82);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0;
  margin: 0.25rem 0;
  text-align: left;
  &:hover {
    color: #fff;
  }
}

.vc-sip-error {
  font-size: 0.72rem;
  color: #fecdd3;
  background: rgba(127, 29, 29, 0.32);
  border: 1px solid rgba(255, 77, 141, 0.34);
  border-radius: 0.7rem;
  padding: 0.35rem 0.6rem;
  margin-top: 0.5rem;
}

// Grid de botones de área (transferencia rápida)
.vc-areas {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.4rem;
  margin-bottom: 0.625rem;
}

.vc-area-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
  padding: 0.45rem 0.5rem;
  border-radius: 0.7rem;
  border: 1px solid rgba(148, 234, 255, 0.16);
  background: rgba(255, 255, 255, 0.055);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: rgba(38, 217, 247, 0.16);
    color: #fff;
    border-color: rgba(38, 217, 247, 0.4);
  }
}

.vc-area-ext {
  font-size: 0.65rem;
  font-family: monospace;
  color: rgba(226, 232, 240, 0.62);
  .vc-area-btn:hover & {
    color: rgba(255, 255, 255, 0.75);
  }
}

// Panel expandible (transferencia / dispositivos)
.vc-panel {
  width: 100%;
  margin-top: 0;
  padding: 0.875rem;
  border-radius: 0.85rem;
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(148, 234, 255, 0.14);
}

.vc-panel-title {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(226, 232, 240, 0.72);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.vc-panel-label {
  display: block;
  font-size: 0.75rem;
  color: rgba(226, 232, 240, 0.72);
  margin-bottom: 0.25rem;
  margin-top: 0.5rem;
}

.vc-input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(148, 234, 255, 0.22);
  border-radius: 0.7rem;
  font-size: 0.875rem;
  outline: none;
  background: rgba(0, 0, 0, 0.22);
  color: #fff;
  &:focus {
    border-color: rgba(38, 217, 247, 0.7);
    box-shadow: 0 0 0 2px rgba(38, 217, 247, 0.14);
  }
}

.vc-panel-btn {
  padding: 0.5rem 0.875rem;
  border-radius: 0.75rem;
  border: none;
  background: linear-gradient(135deg, #8b2ac9, #26d9f7);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s;
  &:hover:not(:disabled) {
    opacity: 0.88;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.vc-select {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(148, 234, 255, 0.22);
  border-radius: 0.7rem;
  font-size: 0.875rem;
  background: rgba(0, 0, 0, 0.22);
  color: #fff;
  outline: none;
  &:focus {
    border-color: rgba(38, 217, 247, 0.7);
    box-shadow: 0 0 0 2px rgba(38, 217, 247, 0.14);
  }
}

@media (max-width: 520px) {
  .vc-incoming-float,
  .vc-float,
  .vc-dialer {
    right: 0.75rem;
    bottom: 0.75rem;
    width: calc(100vw - 1.5rem);
  }

  .vc-row--gap {
    gap: 0.5rem;
  }

  .vc-action-btn,
  .vc-panel-btn {
    min-width: 0;
  }
}
</style>
